import { useEffect, useRef, useState, type SubmitEvent } from 'react'
import {
  Mtds,
  getEnvName,
  toUserMessage,
  useUploadAttachmentMutation,
  MAX_ATTACHMENT_SIZE_BYTES
} from 'prove-common'
import {
  validateAttachment,
  type ResultsReceipt,
  type ToolErrors
} from './resultsUpload'
import { ToolErrorSummary } from './ToolErrorSummary'

export type AttachmentUploadCardProps = {
  receipt?: {
    requisitionId: string
    data: ResultsReceipt
  }
  disabled?: boolean
  resetKey?: number
  onStatusChange?: (status: { dirty: boolean; pending: boolean }) => void
}

export const AttachmentUploadCard = ({
  receipt,
  disabled = false,
  resetKey,
  onStatusChange
}: AttachmentUploadCardProps) => {
  const uploadAttachment = useUploadAttachmentMutation()
  const [manualTarget, setManualTarget] = useState(false)
  const [manualRequisitionId, setManualRequisitionId] = useState('')
  const [manualResultId, setManualResultId] = useState('')
  const [savedManualTarget, setSavedManualTarget] = useState('["",""]')
  const [returnedResultId, setReturnedResultId] = useState(() =>
    receipt?.data.results[0] ? String(receipt.data.results[0].id) : ''
  )
  const [file, setFile] = useState<File>()
  const fileInput = useRef<HTMLInputElement>(null)
  const [attachmentErrors, setAttachmentErrors] = useState<ToolErrors>({})
  const [attachmentError, setAttachmentError] = useState('')
  const [uploaded, setUploaded] = useState<string[]>([])
  const writing = useRef(false)

  const isBusy = disabled || uploadAttachment.isPending
  const dirty =
    !!file ||
    (manualTarget &&
      JSON.stringify([manualRequisitionId, manualResultId]) !==
        savedManualTarget)
  const pending = uploadAttachment.isPending

  useEffect(() => {
    onStatusChange?.({ dirty, pending })
  }, [dirty, pending, onStatusChange])

  const prevReceiptRef = useRef(receipt)
  useEffect(() => {
    if (receipt && receipt !== prevReceiptRef.current) {
      setReturnedResultId(
        receipt.data.results[0] ? String(receipt.data.results[0].id) : ''
      )
      setManualTarget(false)
    }
    prevReceiptRef.current = receipt
  }, [receipt])

  const initialResetKey = useRef(resetKey)
  useEffect(() => {
    if (resetKey !== undefined && resetKey !== initialResetKey.current) {
      initialResetKey.current = resetKey
      setManualTarget(false)
      setManualRequisitionId('')
      setManualResultId('')
      setSavedManualTarget('["",""]')
      setReturnedResultId('')
      setFile(undefined)
      setAttachmentErrors({})
      setAttachmentError('')
      setUploaded([])
      if (fileInput.current) fileInput.current.value = ''
    }
  }, [resetKey])

  const env = getEnvName()
  const production = env === undefined
  const targetRequisition = manualTarget
    ? manualRequisitionId.trim()
    : (receipt?.requisitionId ?? '')
  const targetResult = manualTarget ? manualResultId.trim() : returnedResultId

  const handleUpload = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (writing.current) return
    const nextErrors = validateAttachment(
      targetRequisition,
      targetResult,
      file,
      MAX_ATTACHMENT_SIZE_BYTES
    )
    if (!manualTarget && nextErrors['attachment-requisition']) {
      delete nextErrors['attachment-requisition']
      nextErrors['attachment-result'] =
        'Send resultater først, eller velg et eksisterende resultat med manuelle ID-er.'
    }
    setAttachmentErrors(nextErrors)
    if (Object.keys(nextErrors).length || !file) return
    if (
      !manualTarget &&
      !receipt?.data.results.some(
        (result) => String(result.id) === targetResult
      )
    )
      return
    if (
      production &&
      !window.confirm(
        `PRODUKSJON: Last opp ${file.name} til rekvisisjon ${targetRequisition}, resultat ${targetResult}? Dette skriver et reelt vedlegg.`
      )
    )
      return
    writing.current = true
    setAttachmentError('')
    try {
      const transferred = await uploadAttachment.mutateAsync({
        requisitionId: targetRequisition,
        resultId: targetResult,
        file
      })
      if (transferred !== true) {
        throw new Error('Opplastingen ble ikke bekreftet.')
      }
      setUploaded((current) => [
        ...current,
        `${file.name} → rekvisisjon ${targetRequisition}, resultat ${targetResult}`
      ])
      setFile(undefined)
      if (manualTarget)
        setSavedManualTarget(
          JSON.stringify([manualRequisitionId, manualResultId])
        )
      if (fileInput.current) fileInput.current.value = ''
    } catch (error) {
      setAttachmentError(
        `${toUserMessage(error, 'laste opp vedlegget')} Filen og målet er beholdt. Resultater er ikke sendt på nytt. Kontroller vedleggslisten før nytt forsøk.`
      )
    } finally {
      writing.current = false
    }
  }

  return (
    <Mtds.Card as={Mtds.Grid} data-pad='4'>
      <Mtds.Heading as='h2' data-size='sm'>
        2. Last opp PDF-vedlegg
      </Mtds.Heading>
      <Mtds.Muted>
        Velg fil først, last opp eksplisitt etterpå. Kun PDF, maks 20 MB. En fil
        knyttes til ett resultat om gangen.
      </Mtds.Muted>
      <Mtds.Grid as='form' noValidate onSubmit={handleUpload}>
        <ToolErrorSummary errors={attachmentErrors} />
        <Mtds.Field
          as='input'
          type='checkbox'
          label='Bruk et eksisterende resultat (manuelle ID-er)'
          checked={manualTarget}
          disabled={isBusy}
          onChange={(event) => {
            setManualTarget(event.target.checked)
            setAttachmentErrors({})
          }}
        />
        {manualTarget ? (
          <Mtds.Grid data-items='250'>
            <Mtds.Field
              as='input'
              id='attachment-requisition'
              label='Vedleggets rekvisisjons-ID'
              required
              disabled={isBusy}
              value={manualRequisitionId}
              validation={attachmentErrors['attachment-requisition']}
              onChange={(event) => setManualRequisitionId(event.target.value)}
            />
            <Mtds.Field
              as='input'
              id='attachment-result'
              label='Vedleggets resultat-ID'
              required
              disabled={isBusy}
              value={manualResultId}
              validation={attachmentErrors['attachment-result']}
              onChange={(event) => setManualResultId(event.target.value)}
            />
          </Mtds.Grid>
        ) : (
          <>
            {!receipt && (
              <Mtds.Muted>
                Send resultater først for å få tilgjengelige resultat-ID-er,
                eller velg et eksisterende resultat.
              </Mtds.Muted>
            )}
            <Mtds.Field
              as='select'
              id='attachment-result'
              label='Resultat fra siste bekreftede innsending'
              disabled={isBusy || !receipt}
              value={returnedResultId}
              validation={attachmentErrors['attachment-result']}
              onChange={(event) => setReturnedResultId(event.target.value)}
              options={[
                { value: '', label: 'Velg resultat' },
                ...(receipt?.data.results.map((result) => ({
                  value: String(result.id),
                  label: `${result.id} · ${result.substance || 'uten substans'} · delprøve ${result.subSampleId ?? 'ikke oppgitt'}`
                })) ?? [])
              ]}
            />
          </>
        )}
        <Mtds.Info>
          <strong>Opplastingsmål</strong>Rekvisisjon{' '}
          {targetRequisition || 'ikke valgt'}, resultat{' '}
          {targetResult || 'ikke valgt'}
        </Mtds.Info>
        <Mtds.Field
          as='input'
          ref={fileInput}
          id='attachment-file'
          type='file'
          label='PDF-fil'
          accept='.pdf,application/pdf'
          disabled={isBusy}
          validation={attachmentErrors['attachment-file']}
          onChange={(event) => {
            setFile(event.target.files?.[0])
            setAttachmentErrors({})
            setAttachmentError('')
          }}
        />
        {attachmentError && (
          <Mtds.Alert role='alert' data-color='danger'>
            {attachmentError}
          </Mtds.Alert>
        )}
        <Mtds.Flex>
          <Mtds.Button
            type='submit'
            data-variant='primary'
            aria-busy={uploadAttachment.isPending}
            disabled={isBusy || (!manualTarget && !returnedResultId)}
          >
            {uploadAttachment.isPending
              ? 'Laster opp PDF…'
              : 'Last opp PDF til valgt resultat'}
          </Mtds.Button>
        </Mtds.Flex>
      </Mtds.Grid>
      {!!uploaded.length && (
        <Mtds.Alert data-color='success'>
          <Mtds.Heading as='h3' data-size='xs'>
            Filer overført
          </Mtds.Heading>
          <ul>
            {uploaded.map((message, index) => (
              <li key={index}>{message}</li>
            ))}
          </ul>
          Registrering av vedlegget skjer asynkront og kan ta litt tid.
        </Mtds.Alert>
      )}
    </Mtds.Card>
  )
}
